import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Plus, Edit, RefreshCw, History, Trash2 } from 'lucide-react';
import { ApiService } from '../utils/api';
import { formatDateTime } from '../utils/datetime';
import { useToast } from './Toast';
import ConfirmationDialog from './ui/ConfirmationDialog';

// The app looks these IDs up by name (badge printing, certificates), so they stay fixed.
const FIXED_PURPOSE_IDS = ['badge', 'certificate'];

const NEW_PURPOSE_TEMPLATE = {
  purpose: '',
  name: '',
  description: '',
  shouldPrintBadge: false,
  canUpdateUser: false,
  hasInventory: false,
  allowMultipleScanning : false,
  totalInventory: null
};

const QRScanPurposeManager = ({ eventId }) => {
  const [editingPurpose, setEditingPurpose] = useState(null);
  const [showRefillModal, setShowRefillModal] = useState(false);
  const [refillData, setRefillData] = useState({ quantity: '', reason: '' });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [purposeToDelete, setPurposeToDelete] = useState(null);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  // Fetch QR scan purposes
  const { data: purposes = [] } = useQuery({
    queryKey: ['qrScanPurposes', eventId],
    queryFn: () => ApiService.getQRScanPurposes(eventId),
  });

  // Create/Update purpose mutation
  const updatePurposeMutation = useMutation({
    mutationFn: async ({ originalPurpose, ...data }) => {
      const renamed = originalPurpose && originalPurpose !== data.purpose;
      if (renamed) {
        await ApiService.renameQRScanPurpose(eventId, originalPurpose, data.purpose);
      }
      await ApiService.createOrUpdateQRScanPurpose(eventId, data);
      return { renamed };
    },
    onSuccess: ({ renamed }) => {
      // A rename rewrites check-ins, badge layouts and form blacklists, so refresh everything cached.
      if (renamed) queryClient.invalidateQueries();
      else queryClient.invalidateQueries(['qrScanPurposes', eventId]);
      setEditingPurpose(null);
      showSuccess(renamed ? 'Purpose ID changed and all references updated' : 'QR scan purpose updated successfully!');
    },
    onError: (error) => {
      queryClient.invalidateQueries(['qrScanPurposes', eventId]);
      showError(error?.message || 'Failed to update QR scan purpose');
    }
  });

  const deletePurposeMutation = useMutation({
    mutationFn: (purpose) => ApiService.deleteQRScanPurpose(eventId, purpose),
    onSuccess: () => {
      queryClient.invalidateQueries(['qrScanPurposes', eventId]);
      setPurposeToDelete(null);
      setEditingPurpose(null);
      showSuccess('Scan purpose deleted. Its scan history is kept.');
    },
    onError: (error) => {
      showError(error?.message || 'Failed to delete scan purpose');
    }
  });

  // Refill inventory mutation
  const refillInventoryMutation = useMutation({
    mutationFn: ({ purpose, data }) => ApiService.refillQRScanInventory(eventId, purpose, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['qrScanPurposes', eventId]);
      setShowRefillModal(false);
      showSuccess('Inventory refilled successfully!');
    },
    onError: (error) => {
      showError('Failed to refill inventory');
    }
  });


  const handleSavePurpose = (purpose) => {
    // Validate required fields
    if (!purpose.purpose || !purpose.name) {
      showError('Purpose ID and Display Name are required');
      return;
    }

    // Validate purpose ID format
    const idChanged = purpose.isNew || purpose.purpose !== purpose.originalPurpose;
    if (idChanged && !/^[a-z0-9_]+$/.test(purpose.purpose)) {
      showError('Purpose ID can only contain lowercase letters, numbers, and underscores');
      return;
    }

    if (purposes.some(p => p.purpose === purpose.purpose && (purpose.isNew || p.id !== purpose.id))) {
      showError('Purpose ID already exists. Please choose a different one.');
      return;
    }

    // Prepare data for API
    const purposeData = {
      originalPurpose: purpose.isNew ? null : purpose.originalPurpose,
      purpose: purpose.purpose,
      name: purpose.name,
      description: purpose.description || '',
      shouldPrintBadge: purpose.shouldPrintBadge || false,
      canUpdateUser: purpose.canUpdateUser || false,
      hasInventory: purpose.hasInventory || false,
      allowMultipleScanning: purpose.allowMultipleScanning || false,
      totalInventory: purpose.hasInventory ? (purpose.totalInventory || null) : null
    };

    // Send to API
    updatePurposeMutation.mutate(purposeData);
  };

  const handleRefillInventory = (purpose, quantity, reason) => {
    refillInventoryMutation.mutate({
      purpose: purpose.purpose,
      data: {
        quantity,
        reason
      }
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>QR Scan Purposes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Add New Purpose Button */}
            <Button
              variant="outline"
              onClick={() => setEditingPurpose({ ...NEW_PURPOSE_TEMPLATE, isNew: true })}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Purpose
            </Button>

            {/* Purpose List */}
            <div className="space-y-4">
              {purposes.map(purpose => (
                <Card key={purpose.id} className="bg-gray-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">{purpose.name}</h3>
                        <p className="text-sm text-gray-600">{purpose.description}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {purpose.hasInventory && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowRefillModal(purpose)}
                            >
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Refill
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowHistoryModal(purpose)}
                            >
                              <History className="h-4 w-4 mr-1" />
                              History
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingPurpose({ ...purpose, originalPurpose: purpose.purpose })}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>

                    {/* Purpose Stats */}
                    <div className="mt-4 grid grid-cols-3 gap-4">
                      <div>
                        <Label>Total Scans</Label>
                        <p className="text-lg font-medium">{purpose.totalScans || 0}</p>
                      </div>
                      {purpose.hasInventory && (
                        <>
                          <div>
                            <Label>Total Inventory</Label>
                            <p className="text-lg font-medium">
                              {purpose.totalInventory === null ? '∞' : purpose.totalInventory}
                            </p>
                          </div>
                          <div>
                            <Label>Remaining</Label>
                            <p className="text-lg font-medium">
                              {purpose.totalInventory === null ? '∞' : purpose.remainingInventory}
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Settings */}
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Print Badge</Label>
                        <Switch
                          checked={purpose.shouldPrintBadge}
                          disabled={true}
                          onCheckedChange={() => {}}
                          className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-gray-200 transition-all duration-200 ease-in-out data-[state=checked]:shadow-inner"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Allow User Update</Label>
                        <Switch
                          checked={purpose.canUpdateUser}
                          disabled={true}
                          onCheckedChange={() => {}}
                          className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-gray-200 transition-all duration-200 ease-in-out data-[state=checked]:shadow-inner"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Track Inventory</Label>
                        <Switch
                          checked={purpose.hasInventory}
                          disabled={true}
                          onCheckedChange={() => {}}
                          className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-gray-200 transition-all duration-200 ease-in-out data-[state=checked]:shadow-inner"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Allow Multiple Scanning</Label>
                        <Switch
                          checked={purpose.allowMultipleScanning}
                          disabled={true}
                          onCheckedChange={() => {}}
                          className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-gray-200 transition-all duration-200 ease-in-out data-[state=checked]:shadow-inner"
                        />
                      </div>
                      {purpose.hasInventory && (
                        <div>
                          <Label>Total Inventory</Label>
                          <Input
                            type="number"
                            value={purpose.totalInventory || ''}
                            disabled={true}
                            placeholder="Leave empty for unlimited"
                          />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Refill Modal */}
      {showRefillModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Refill Inventory - {showRefillModal.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={refillData.quantity}
                    placeholder="Enter quantity to add"
                    onChange={(e) => setRefillData(prev => ({
                      ...prev,
                      quantity: e.target.value
                    }))}
                  />
                </div>
                <div>
                  <Label>Reason</Label>
                  <Input
                    value={refillData.reason}
                    placeholder="Enter reason for refill"
                    onChange={(e) => setRefillData(prev => ({
                      ...prev,
                      reason: e.target.value
                    }))}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowRefillModal(false);
                      setRefillData({ quantity: '', reason: '' });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => {
                      if (!refillData.quantity || !refillData.reason) {
                        showError('Quantity and reason are required');
                        return;
                      }
                      handleRefillInventory(showRefillModal, parseInt(refillData.quantity), refillData.reason);
                      setRefillData({ quantity: '', reason: '' });
                    }}
                    disabled={refillInventoryMutation.isPending}
                  >
                    {refillInventoryMutation.isPending ? 'Refilling...' : 'Refill Inventory'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Inventory History - {showHistoryModal.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* History Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Date</th>
                        <th className="text-left py-2">Quantity</th>
                        <th className="text-left py-2">Reason</th>
                        <th className="text-left py-2">Added By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {showHistoryModal.inventoryHistory?.map((refill) => (
                        <tr key={refill.id} className="border-b">
                          <td className="py-2">
                            {formatDateTime(refill.refillDate)}
                          </td>
                          <td className="py-2">
                            +{refill.quantity}
                          </td>
                          <td className="py-2">
                            {refill.reason}
                          </td>
                          <td className="py-2">
                            {refill.actorId}
                          </td>
                        </tr>
                      ))}
                      {(!showHistoryModal.inventoryHistory || showHistoryModal.inventoryHistory.length === 0) && (
                        <tr>
                          <td colSpan="4" className="text-center py-4 text-gray-500">
                            No refill history available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end mt-4">
                  <Button variant="outline" onClick={() => setShowHistoryModal(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit/Create Purpose Modal */}
      {editingPurpose && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editingPurpose.isNew ? 'Add New Purpose' : 'Edit Purpose'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Purpose ID */}
                <div>
                  <Label>Purpose ID *</Label>
                  <Input
                    value={editingPurpose.purpose}
                    onChange={(e) => setEditingPurpose(prev => ({
                      ...prev,
                      purpose: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
                    }))}
                    placeholder="Enter a unique identifier (e.g., badge_gen, lunch_1)"
                    disabled={!editingPurpose.isNew && FIXED_PURPOSE_IDS.includes(editingPurpose.originalPurpose)}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {editingPurpose.isNew
                      ? 'Unique identifier, use lowercase letters, numbers, and underscores only'
                      : FIXED_PURPOSE_IDS.includes(editingPurpose.originalPurpose)
                        ? 'This system purpose ID cannot be changed'
                        : 'Lowercase letters, numbers, and underscores only'}
                  </p>
                  {!editingPurpose.isNew && editingPurpose.purpose !== editingPurpose.originalPurpose && (
                    <p className="text-sm text-amber-700 mt-1">
                      Existing check-ins, badge layout and form restrictions will move to the new ID.
                      Scan links that use <span className="font-mono">?purpose={editingPurpose.originalPurpose}</span> must be updated.
                    </p>
                  )}
                </div>

                {/* Display Name */}
                <div>
                  <Label>Display Name *</Label>
                  <Input
                    value={editingPurpose.name}
                    onChange={(e) => setEditingPurpose(prev => ({
                      ...prev,
                      name: e.target.value
                    }))}
                    placeholder="Enter a user-friendly name"
                  />
                </div>

                {/* Description */}
                <div>
                  <Label>Description</Label>
                  <Input
                    value={editingPurpose.description}
                    onChange={(e) => setEditingPurpose(prev => ({
                      ...prev,
                      description: e.target.value
                    }))}
                    placeholder="Enter a brief description"
                  />
                </div>

                {/* Settings */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Print Badge</Label>
                      <p className="text-sm text-gray-500">Automatically print badge after successful scan</p>
                    </div>
                    <Switch
                      checked={editingPurpose.shouldPrintBadge}
                      onCheckedChange={(checked) => setEditingPurpose(prev => ({
                        ...prev,
                        shouldPrintBadge: checked
                      }))}
                      className="data-[state=checked]:bg-blue-600 transition-colors duration-200 ease-in-out hover:opacity-90 data-[state=unchecked]:hover:bg-gray-200"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Allow User Update</Label>
                      <p className="text-sm text-gray-500">Show edit button after successful scan</p>
                    </div>
                    <Switch
                      checked={editingPurpose.canUpdateUser}
                      onCheckedChange={(checked) => setEditingPurpose(prev => ({
                        ...prev,
                        canUpdateUser: checked
                      }))}
                      className="data-[state=checked]:bg-blue-600 transition-colors duration-200 ease-in-out hover:opacity-90 data-[state=unchecked]:hover:bg-gray-200"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Track Inventory</Label>
                      <p className="text-sm text-gray-500">Enable inventory management for this purpose</p>
                    </div>
                    <Switch
                      checked={editingPurpose.hasInventory}
                      onCheckedChange={(checked) => setEditingPurpose(prev => ({
                        ...prev,
                        hasInventory: checked,
                        totalInventory: checked ? prev.totalInventory || 0 : null
                      }))}
                      className="data-[state=checked]:bg-blue-600 transition-colors duration-200 ease-in-out hover:opacity-90 data-[state=unchecked]:hover:bg-gray-200"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Allow Multiple Scanning</Label>
                      <p className="text-sm text-gray-500">Allow the same QR code to be scanned multiple times</p>
                    </div>
                    <Switch
                      checked={editingPurpose.allowMultipleScanning}
                      onCheckedChange={(checked) => setEditingPurpose(prev => ({
                        ...prev,
                        allowMultipleScanning: checked
                      }))}
                      className="data-[state=checked]:bg-blue-600 transition-colors duration-200 ease-in-out hover:opacity-90 data-[state=unchecked]:hover:bg-gray-200"
                    />
                  </div>

                  {editingPurpose.hasInventory && (
                    <div>
                      <Label>Initial Inventory</Label>
                      <Input
                        type="number"
                        min="0"
                        value={editingPurpose.totalInventory || ''}
                        onChange={(e) => setEditingPurpose(prev => ({
                          ...prev,
                          totalInventory: e.target.value === '' ? null : parseInt(e.target.value)
                        }))}
                        placeholder="Leave empty for unlimited"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Set to 0 or empty for unlimited inventory
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-2 pt-4">
                  {!editingPurpose.isNew && (
                    <Button
                      variant="outline"
                      className="mr-auto text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => setPurposeToDelete(editingPurpose)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete Purpose
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setEditingPurpose(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (!editingPurpose.purpose || !editingPurpose.name) {
                        showError('Purpose ID and Display Name are required');
                        return;
                      }
                      handleSavePurpose(editingPurpose);
                    }}
                    disabled={updatePurposeMutation.isPending}
                  >
                    {updatePurposeMutation.isPending ? 'Saving...' : 'Save Purpose'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      <ConfirmationDialog
        isOpen={!!purposeToDelete}
        onClose={() => !deletePurposeMutation.isPending && setPurposeToDelete(null)}
        onConfirm={() => deletePurposeMutation.mutate(purposeToDelete.originalPurpose || purposeToDelete.purpose)}
        title="Delete scan purpose?"
        message={purposeToDelete
          ? `"${purposeToDelete.name}" will be removed from scanning and purpose lists. Check-ins already recorded stay on registrations and in analytics.`
          : ''}
        confirmText="Delete Purpose"
        isLoading={deletePurposeMutation.isPending}
      />
    </div>
  );
};

export default QRScanPurposeManager;
